import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import StatusDot from "@/components/StatusDot/StatusDot"
import {
  NAV_ALL_CUSTOMERS,
  NAV_ARCHIVE,
  NAV_ALL_SESSIONS,
  NAV_CUSTOMERS,
  NAV_DECK,
  NAV_NO_CUSTOMERS,
  NAV_NO_SESSIONS,
  NAV_REPORTS,
  NAV_SEARCH,
  NAV_SESSIONS,
  SEARCH_EMPTY,
  SEARCH_PLACEHOLDER,
  SEARCH_SCOPE,
} from "@/content/nav"
import { UNTITLED_SESSION } from "@/content/customers"
import { ROUTES } from "@/routes/routes"
import Badge from "@/components/Badge/Badge"
import Spinner from "@/components/Spinner/Spinner"
import { useAuthStore, selectAuthUser } from "@/store/authStore"
import { signOut } from "@/store/authSync"
import { customerDetailPath, sessionPath } from "@/utils/routePaths"
import { sessionState } from "@/utils/sessionLifecycle"
import { useExpiryTick } from "@/hooks/useExpiryTick"
import type { FacilitatorIndex } from "@/hooks/useFacilitatorIndex"
import styles from "@/styles/components/TopNav.module.css"

/**
 * The floating nav pill, top-right of every facilitator screen.
 *
 * The two carets open the facilitator's own recent customers and sessions — the
 * lists `AppShell` already loaded, so opening a menu costs no read. Deck and
 * Reports are plain destinations.
 *
 * The search button filters those same two lists IN MEMORY. It is deliberately
 * not a Firestore query: there is no full-text index, and the honest thing is a
 * control that says what it searches rather than one that looks global and
 * silently is not — hence `SEARCH_SCOPE` under the field.
 */

type OpenMenu = "customers" | "sessions" | "search" | "account" | null

const RECENT = 5

interface TopNavProps {
  index: FacilitatorIndex
}

export default function TopNav({ index }: TopNavProps) {
  const navigate = useNavigate()
  const me = useAuthStore(selectAuthUser)
  const [open, setOpen] = useState<OpenMenu>(null)
  const [term, setTerm] = useState("")
  const [signingOut, setSigningOut] = useState(false)
  const wrap = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(null)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(null)
    }
    document.addEventListener("mousedown", onDown)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDown)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  const go = (path: string) => {
    setOpen(null)
    setTerm("")
    navigate(path)
  }

  const customers = index.customers ?? []
  const sessions = index.sessions ?? []
  // The pill outlives every navigation, so an open menu would otherwise keep
  // showing Live for a session whose deadline passed while it was open.
  const now = useExpiryTick(sessions.map((session) => session.expiresAt))
  const needle = term.trim().toLowerCase()
  const hitCustomers = needle
    ? customers.filter((customer) => customer.name.toLowerCase().includes(needle))
    : []
  const hitSessions = needle
    ? sessions.filter(
        (session) =>
          session.name.toLowerCase().includes(needle) ||
          session.customerName.toLowerCase().includes(needle)
      )
    : []

  return (
    <nav className={styles.nav} ref={wrap} aria-label="Main">
      <div className={styles.pill}>
        <div className={styles.anchor}>
          <button
            type="button"
            className={styles.item}
            aria-expanded={open === "customers"}
            aria-haspopup="menu"
            onClick={() => setOpen((current) => (current === "customers" ? null : "customers"))}
          >
            {NAV_CUSTOMERS}
            <Caret open={open === "customers"} />
          </button>
          {open === "customers" ? (
            <div className={`${styles.menu} ${styles.menuUnder}`} role="menu">
              {customers.length === 0 ? (
                <p className={styles.menuEmpty}>{NAV_NO_CUSTOMERS}</p>
              ) : (
                customers.slice(0, RECENT).map((customer) => (
                  <button
                    key={customer.id}
                    type="button"
                    role="menuitem"
                    className={styles.menuItem}
                    onClick={() => go(customerDetailPath(customer.id))}
                  >
                    {customer.name || "Untitled customer"}
                  </button>
                ))
              )}
              <button
                type="button"
                role="menuitem"
                className={`${styles.menuItem} ${styles.menuAll}`}
                onClick={() => go(ROUTES.ROOT)}
              >
                {NAV_ALL_CUSTOMERS}
              </button>
            </div>
          ) : null}
        </div>

        <div className={styles.anchor}>
          <button
            type="button"
            className={styles.item}
            aria-expanded={open === "sessions"}
            aria-haspopup="menu"
            onClick={() => setOpen((current) => (current === "sessions" ? null : "sessions"))}
          >
            {NAV_SESSIONS}
            <Caret open={open === "sessions"} />
          </button>
          {open === "sessions" ? (
            <div className={`${styles.menu} ${styles.menuWide} ${styles.menuUnder}`} role="menu">
              {sessions.length === 0 ? (
                <p className={styles.menuEmpty}>{NAV_NO_SESSIONS}</p>
              ) : (
                sessions.slice(0, RECENT).map((session) => (
                  <button
                    key={session.id}
                    type="button"
                    role="menuitem"
                    className={styles.menuItem}
                    onClick={() => go(sessionPath(session.id))}
                  >
                    <span className={styles.menuLabel}>{session.name || UNTITLED_SESSION}</span>
                    <StatusDot state={sessionState(session, now)} compact />
                  </button>
                ))
              )}
              <button
                type="button"
                role="menuitem"
                className={`${styles.menuItem} ${styles.menuAll}`}
                onClick={() => go(ROUTES.SESSIONS)}
              >
                {NAV_ALL_SESSIONS}
              </button>
            </div>
          ) : null}
        </div>

        <button type="button" className={styles.item} onClick={() => go(ROUTES.DECK)}>
          {NAV_DECK}
        </button>

        <button type="button" className={styles.item} onClick={() => go(ROUTES.REPORTS)}>
          {NAV_REPORTS}
        </button>

        <button type="button" className={styles.item} onClick={() => go(ROUTES.ARCHIVE)}>
          {NAV_ARCHIVE}
        </button>
      </div>

      <button
        type="button"
        className={styles.search}
        aria-label={NAV_SEARCH}
        aria-expanded={open === "search"}
        onClick={() => setOpen((current) => (current === "search" ? null : "search"))}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M10.5 10.5 14 14"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {/* The only way out of the app. The design's nav has no account control at
          all, and shipping it that way would leave a signed-in facilitator with
          no way to sign out. */}
      {me ? (
        <button
          type="button"
          className={styles.account}
          aria-label={me.displayName || me.email}
          aria-expanded={open === "account"}
          onClick={() => setOpen((current) => (current === "account" ? null : "account"))}
        >
          <Badge name={me.displayName || me.email} size={36} />
        </button>
      ) : null}

      {open === "account" && me ? (
        <div className={styles.menu}>
          <p className={styles.accountName}>{me.displayName || me.email}</p>
          <p className={styles.accountEmail}>{me.email}</p>
          <button
            type="button"
            className={`${styles.menuItem} ${styles.menuAll}`}
            disabled={signingOut}
            aria-busy={signingOut || undefined}
            onClick={() => {
              setSigningOut(true)
              // No reset on success: signing out unmounts the whole shell. The
              // catch is only there for the case where it does not.
              void signOut().catch(() => setSigningOut(false))
            }}
          >
            {signingOut ? <Spinner size="sm" /> : null}
            Sign out
          </button>
        </div>
      ) : null}

      {open === "search" ? (
        <div className={`${styles.menu} ${styles.menuSearch}`}>
          <input
            className={styles.field}
            type="search"
            autoFocus
            value={term}
            placeholder={SEARCH_PLACEHOLDER}
            aria-label={SEARCH_PLACEHOLDER}
            onChange={(event) => setTerm(event.target.value)}
          />
          <p className={styles.scope}>{SEARCH_SCOPE}</p>

          {needle && hitCustomers.length === 0 && hitSessions.length === 0 ? (
            <p className={styles.menuEmpty}>{SEARCH_EMPTY}</p>
          ) : null}

          {hitCustomers.slice(0, RECENT).map((customer) => (
            <button
              key={customer.id}
              type="button"
              className={styles.menuItem}
              onClick={() => go(customerDetailPath(customer.id))}
            >
              <span className={styles.menuLabel}>{customer.name}</span>
              <span className={styles.menuKind}>{NAV_CUSTOMERS}</span>
            </button>
          ))}

          {hitSessions.slice(0, RECENT).map((session) => (
            <button
              key={session.id}
              type="button"
              className={styles.menuItem}
              onClick={() => go(sessionPath(session.id))}
            >
              <span className={styles.menuLabel}>{session.name || UNTITLED_SESSION}</span>
              <span className={styles.menuKind}>{session.customerName}</span>
            </button>
          ))}
        </div>
      ) : null}
    </nav>
  )
}

function Caret({ open }: { open: boolean }) {
  return (
    <svg
      className={open ? `${styles.caret} ${styles.caretOpen}` : styles.caret}
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M4 6.5 8 10.5 12 6.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
